import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import React, { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { ConversationTurn } from '../api';
import { messages } from '../messages';
import { formatConversationDate } from './formatConversationDate';
import { useStyles } from './styles';

export interface TurnRowProps {
  turn: ConversationTurn;
  onOpen?: (turn: ConversationTurn) => void;
  isOpen?: boolean;
  renderAction?: (turn: ConversationTurn) => ReactNode;
}

const channels = {
  widget: messages.channelWidget,
  sms: messages.channelSms,
  whatsapp: messages.channelWhatsapp,
  email: messages.channelEmail,
};

export const TurnRow = ({
  turn,
  onOpen,
  isOpen,
  renderAction,
}: TurnRowProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const kind = turn.applicant.kind;
  const anonymized = kind === 'anonymized';
  const name =
    kind === 'applicant'
      ? turn.applicant.name
      : intl.formatMessage(
          anonymized ? messages.anonymizedApplicant : messages.visitorApplicant,
        );
  const opening =
    kind === 'visitor'
      ? intl.formatMessage(messages.filterNoOpening)
      : (turn.opening?.title ?? intl.formatMessage(messages.unknownOpening));
  const date = formatConversationDate(turn.asked_at, intl.locale);
  const channel = channels[turn.channel as keyof typeof channels];
  const answer = turn.reply
    .flatMap(part => (part.type === 'text' ? [part.text] : []))
    .join('\n');

  return (
    <div
      className={classNames(classes.grid, classes.row, {
        [classes.open]: isOpen,
      })}
      role="button"
      tabIndex={0}
      aria-label={`${name}, ${date}`}
      aria-current={isOpen ? 'true' : undefined}
      onClick={event => {
        if (
          event.target instanceof Element &&
          event.target.closest('[data-conversations-action]')
        ) {
          event.stopPropagation();
          return;
        }
        onOpen?.(turn);
      }}
      onKeyDown={event => {
        if (
          event.target === event.currentTarget &&
          (event.key === 'Enter' || event.key === ' ')
        ) {
          event.preventDefault();
          onOpen?.(turn);
        }
      }}
    >
      <div>
        <Typography
          variant={kind === 'applicant' ? 'h4' : 'body2'}
          className={kind !== 'applicant' ? classes.secondary : undefined}
        >
          {name}
        </Typography>
        <Typography variant="body2" className={classes.opening}>
          {opening}
        </Typography>
      </div>
      <Typography className={classNames(classes.paragraph, classes.question)}>
        {!anonymized && turn.question}
      </Typography>
      <div className={classes.answer}>
        {!anonymized && (
          <>
            <Typography
              className={classNames(classes.paragraph, classes.answerText, {
                [classes.muted]: !turn.answered,
              })}
            >
              {turn.answered ? answer : intl.formatMessage(messages.noAnswer)}
            </Typography>
            {(turn.thumbs_down || turn.knowledge_gap) && (
              <div className={classes.badges}>
                {turn.thumbs_down && (
                  <span
                    className={classNames(classes.badge, classes.thumbsDown)}
                  >
                    {intl.formatMessage(messages.filterThumbsDown)}
                  </span>
                )}
                {turn.knowledge_gap && (
                  <span
                    className={classNames(classes.badge, classes.knowledgeGap)}
                  >
                    {intl.formatMessage(messages.filterKnowledgeGap)}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div className={classes.date}>
        <Typography variant="body2" className={classes.secondary}>
          {date}
        </Typography>
        {channel && (
          <Typography variant="body2" className={classes.channel}>
            {intl.formatMessage(channel)}
          </Typography>
        )}
      </div>
      <div className={classes.action} data-conversations-action>
        {renderAction?.(turn)}
      </div>
    </div>
  );
};
